import { CORE_DISPUTE_NO_SPECIFIC_MILESTONE, CoreClientReleaseReason, CoreDesiredOutcome, CoreDisputeIssueType, CoreHeldBackAmountType } from "@handshake/core-types";
import type { CoreCreateDisputeDto } from "@handshake/core-types";
import type { Observable } from "rxjs";

export interface CoreDesiredOutcomeOption {
  value: CoreDesiredOutcome;
  label: string;
  requiresReason: boolean;
  reasonLabel?: string;
  reasonPlaceholder?: string;
  /** Extra required single-choice question shown above the reason box (e.g. the client's reason for ID release-funds disputes). */
  clientReleaseReasonQuestion?: CoreClientReleaseReasonQuestion;
  /**
   * HAN-692: ask "How much is being held back?" (full balance in escrow for the milestone, or another amount).
   * The reason box (reasonLabel) is then only shown, with an amount field, when "Other" is chosen.
   */
  heldBackAmountQuestion?: boolean;
}

export interface CoreClientReleaseReasonQuestion {
  label: string;
  options: { value: CoreClientReleaseReason; label: string }[];
}

export const CORE_CLIENT_RELEASE_REASON_OPTIONS: { value: CoreClientReleaseReason; label: string }[] = [
  { value: CoreClientReleaseReason.DEFECTS_WORKMANSHIP, label: "Defects or workmanship" },
  { value: CoreClientReleaseReason.OTHER, label: "Other" },
];

/** Display label for a stored dispute.clientReleaseReason, or null when not set / unknown. */
export function getClientReleaseReasonLabel(value: string | null | undefined): string | null {
  if (!value) return null;
  return CORE_CLIENT_RELEASE_REASON_OPTIONS.find((o) => o.value === value)?.label ?? null;
}

/** "Type of issue" choices for clients of Interior Design (ID) companies (HAN-638). */
export const CORE_DISPUTE_ISSUE_TYPE_OPTIONS: { value: CoreDisputeIssueType; label: string }[] = [
  { value: CoreDisputeIssueType.DEFECTS_WORKMANSHIP, label: "Defects or workmanship" },
  { value: CoreDisputeIssueType.PAYMENT_VARIATION, label: "Payment or variation" },
  { value: CoreDisputeIssueType.SCOPE_DELAY, label: "Scope or delay" },
  { value: CoreDisputeIssueType.OTHER, label: "Other" },
];

/** Display label for a stored dispute.issueType, or null when not set / unknown. */
export function getDisputeIssueTypeLabel(value: string | null | undefined): string | null {
  if (!value) return null;
  return CORE_DISPUTE_ISSUE_TYPE_OPTIONS.find((o) => o.value === value)?.label ?? null;
}

/** One choice of "Which milestone is this about?" (HAN-637): a milestone id, or CORE_DISPUTE_NO_SPECIFIC_MILESTONE. */
export interface CoreDisputeMilestoneOption {
  value: string;
  label: string;
}

/** Choice for a dispute not about one specific milestone; a dispute covering more than one milestone uses it too. */
export const CORE_DISPUTE_NO_SPECIFIC_MILESTONE_OPTION: CoreDisputeMilestoneOption = {
  value: CORE_DISPUTE_NO_SPECIFIC_MILESTONE,
  label: "Not about one specific milestone (or about more than one)",
};

/**
 * "Which milestone is this about?" choices: the project's milestones in start-date order (undated last, as on the
 * project pages), then the "not about one specific milestone" choice.
 */
export function buildDisputeMilestoneOptions(
  milestones: { id?: string | null; name?: string | null; startDate?: string | Date | null }[] | null | undefined
): CoreDisputeMilestoneOption[] {
  const time = (d: string | Date | null | undefined) => (d ? new Date(d).getTime() || Infinity : Infinity);
  const options = [...(milestones ?? [])]
    .filter((m): m is { id: string; name?: string | null; startDate?: string | Date | null } => !!m?.id)
    .sort((a, b) => time(a.startDate) - time(b.startDate))
    .map((m) => ({ value: m.id, label: m.name?.trim() || "Untitled milestone" }));
  return [...options, CORE_DISPUTE_NO_SPECIFIC_MILESTONE_OPTION];
}

/**
 * "Milestone in question" line on the dispute details, shown to both parties (HAN-637).
 * Null for disputes raised before the question existed.
 */
export function getMilestoneInQuestionLabel(dispute: {
  milestoneInQuestionName?: string | null;
  noSpecificMilestone?: boolean | null;
} | null | undefined): string | null {
  if (!dispute) return null;
  if (dispute.noSpecificMilestone) return "No specific milestone";
  return dispute.milestoneInQuestionName?.trim() || null;
}

/** "How much is being held back?" choices (HAN-692). Full balance is left out when it doesn't apply. */
export const CORE_HELD_BACK_AMOUNT_OPTIONS: { value: CoreHeldBackAmountType; label: string }[] = [
  { value: CoreHeldBackAmountType.FULL_BALANCE, label: "Full balance in escrow for this milestone" },
  { value: CoreHeldBackAmountType.OTHER, label: "Other" },
];

/** Money as shown on the dispute form and page, e.g. "SGD 3,838.50" (no currency prefix when unknown). */
export function formatDisputeAmount(amount: number | string | null | undefined, currency?: string | null): string {
  const formatted = (Number(amount) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return currency ? `${currency} ${formatted}` : formatted;
}

/**
 * "Amount held back" line on the dispute details, shown to both parties (HAN-692).
 * Null for disputes without a recorded amount (other outcomes, client disputes, disputes raised before HAN-692).
 */
export function getHeldBackAmountLabel(
  dispute: { heldBackAmount?: number | string | null; heldBackAmountType?: string | null } | null | undefined,
  currency?: string | null
): string | null {
  if (dispute?.heldBackAmount == null) return null;
  const amount = formatDisputeAmount(dispute.heldBackAmount, currency);
  return dispute.heldBackAmountType === CoreHeldBackAmountType.FULL_BALANCE ? `${amount} (full balance in escrow for this milestone)` : amount;
}

export interface CoreRaiseDisputeModalConfig {
  projectMilestoneId: string;
  /** "Which milestone is this about?" choices (HAN-637) — build with buildDisputeMilestoneOptions. */
  milestoneOptions: CoreDisputeMilestoneOption[];
  desiredOutcomeOptions: CoreDesiredOutcomeOption[];
  /**
   * When set, the form asks a required "Type of issue" question above "What's the issue?".
   * Only clients of Interior Design (ID) companies pass this (HAN-638); everyone else leaves it out.
   */
  issueTypeOptions?: { value: CoreDisputeIssueType; label: string }[];
  /**
   * HAN-692: balance in escrow per milestone id, for "How much is being held back?". A milestone missing here
   * counts as SGD 0 (full balance is not offered). Only business hosts pass this.
   */
  milestoneHeldInEscrow?: Record<string, number>;
  /** Project currency, shown with amounts (e.g. "SGD"). */
  currency?: string | null;
  /** Host provides the HTTP call; component handles loading state and modal close/error. */
  submitRequest: (data: CoreCreateDisputeDto, files: File[]) => Observable<unknown>;
}

export const CORE_BUSINESS_DISPUTE_CONFIG: CoreDesiredOutcomeOption[] = [
  {
    value: CoreDesiredOutcome.CLIENT_RELEASE_FUNDS,
    label: "Client to release funds",
    requiresReason: true,
    reasonLabel: "What reason is the client giving for holding back this amount?",
    reasonPlaceholder: "State the reason the client has given",
    heldBackAmountQuestion: true,
  },
  {
    value: CoreDesiredOutcome.OTHER,
    label: "Other",
    requiresReason: true,
    reasonLabel: "State your Desired Outcome",
    reasonPlaceholder: "Be clear on your desired outcome and justifications",
  },
];

/**
 * Reason box label for "Client to release funds" shown to Interior Design (ID) businesses (HAN-639). Since HAN-692
 * the amount has its own field, and this box only appears when "Other" is chosen as the amount held back.
 */
export const CORE_BUSINESS_ID_RELEASE_FUNDS_LABEL = "What details has the client given?";

/** Hint for the ID "Client to release funds" reason box; takes priority over the generic business hint above. */
export const CORE_BUSINESS_ID_RELEASE_FUNDS_PLACEHOLDER = "State any details the client has given (e.g. which defects, where, and when they were raised)";

/**
 * Business dispute options for Interior Design (ID) businesses. Same as CORE_BUSINESS_DISPUTE_CONFIG except
 * "Client to release funds", which also asks for the client's reason and words the text box differently.
 */
export const CORE_BUSINESS_ID_DISPUTE_CONFIG: CoreDesiredOutcomeOption[] = CORE_BUSINESS_DISPUTE_CONFIG.map((option) =>
  option.value === CoreDesiredOutcome.CLIENT_RELEASE_FUNDS
    ? {
        ...option,
        reasonLabel: CORE_BUSINESS_ID_RELEASE_FUNDS_LABEL,
        reasonPlaceholder: CORE_BUSINESS_ID_RELEASE_FUNDS_PLACEHOLDER,
        clientReleaseReasonQuestion: { label: "What reason is the client giving?", options: CORE_CLIENT_RELEASE_REASON_OPTIONS },
      }
    : option
);

export const CORE_CLIENT_DISPUTE_CONFIG: CoreDesiredOutcomeOption[] = [
  {
    value: CoreDesiredOutcome.REVISION_REWORK,
    label: "Revision / Rework requested (work needs changes)",
    requiresReason: true,
    reasonLabel: "What needs to be changed?",
    reasonPlaceholder: "Describe what revisions or rework is needed",
  },
  {
    value: CoreDesiredOutcome.PARTIAL_REFUND,
    label: "Partial refund (for incomplete or unsatisfactory work)",
    requiresReason: true,
    reasonLabel: "What is the requested refund amount and why?",
    reasonPlaceholder: "State the refund amount and explain why a partial refund is justified",
  },
  {
    value: CoreDesiredOutcome.FULL_REFUND,
    label: "Full refund (work does not meet requirements)",
    requiresReason: true,
    reasonLabel: "What is the requested refund amount and why?",
    reasonPlaceholder: "State the refund amount and explain why a full refund is justified",
  },
  {
    value: CoreDesiredOutcome.OTHER,
    label: "Other (specify your desired outcome)",
    requiresReason: true,
    reasonLabel: "State your Desired Outcome",
    reasonPlaceholder: "Be clear on your desired outcome and justifications",
  },
];

/**
 * Merged desired-outcome options for the dispute detail view so all outcome types resolve (client + business).
 * For OTHER we use the label from the dispute creator's config so "Other" vs "Other (specify your desired outcome)" is correct.
 */
export function getMergedDesiredOutcomeOptionsForDisputeDetail(
  createdByRole: "client" | "business"
): CoreDesiredOutcomeOption[] {
  const clientOther = CORE_CLIENT_DISPUTE_CONFIG.find((o) => o.value === CoreDesiredOutcome.OTHER);
  const businessOther = CORE_BUSINESS_DISPUTE_CONFIG.find((o) => o.value === CoreDesiredOutcome.OTHER);
  const otherOption = createdByRole === "client" ? clientOther : businessOther;
  return [
    ...CORE_CLIENT_DISPUTE_CONFIG.filter((o) => o.value !== CoreDesiredOutcome.OTHER),
    ...CORE_BUSINESS_DISPUTE_CONFIG.filter((o) => o.value !== CoreDesiredOutcome.OTHER),
    ...(otherOption ? [otherOption] : []),
  ];
}
